import { assertNever } from "@ondo/shared";
import { Link } from "react-router";

import { LoadingScreen } from "../lib/Loading.tsx";
import { ProvenancePanel } from "./ProvenancePanel.tsx";
import {
  ArtistMatchPanel,
  ArtworkDetailsForm,
  ConfirmPanel,
  UploadImagesPanel,
} from "./RegisterPanels.tsx";
import { RegisterSteps } from "./RegisterSteps.tsx";
import { REGISTER_COPY, REGISTER_FAQ } from "./register.constants.ts";
import { useRegisterController } from "./useRegisterController.ts";
import "./register.css";

/**
 * Registering a work: four steps, then an authority.
 *
 * The work is created here, outright, in the collection named on step 2. There
 * is no submission, no reference and nobody it waits on — 20260804090700
 * removed all three. What the confirm step says instead is the thing that is
 * true: the record is yours immediately, and what it is worth is who will put
 * their name to its provenance.
 *
 * The four steps stay a stage union with one URL over them, deliberately. A
 * step is not a place: `…/register/provenance` on a cold load is a Provenance
 * panel over an empty form with no server-side draft behind it.
 */
export function RegisterArtwork({
  collectionHref,
  workBase,
}: {
  collectionHref: string;
  /** '/collector/me/work' — where the record just created lives. */
  workBase: string;
}) {
  const controller = useRegisterController();
  const { view } = controller;

  if (view.kind === "loading") {
    return <LoadingScreen message={REGISTER_COPY.loading} />;
  }

  if (view.kind === "no-target") {
    return (
      <section className="register">
        <p className="collection__note">{view.message}</p>
      </section>
    );
  }

  if (view.kind === "registered") {
    return (
      <section className="register">
        <div className="register__panel">
          <h2 className="register__panel-title">{view.message}</h2>
          <p className="register__help">
            <strong>{view.title}</strong> is in your collection. What it is worth
            now is who will put their name to its provenance — start by asking
            somebody on its record.
          </p>
          {/* An ONDO id that does not exist is only answerable at submit —
              there is no directory to check it against first — so an ask can
              fail while the work itself is created. This is the only screen the
              collector sees next, so it is where that is said. */}
          {view.notices.length > 0 && (
            <ul className="register__refusals" role="alert">
              {view.notices.map((notice) => (
                <li key={notice}>{notice}</li>
              ))}
            </ul>
          )}
          <Link to={`${workBase}/${view.workId}`} className="register__next">
            Open the record
          </Link>
          <Link to={collectionHref} className="register__back">
            Back to my collection
          </Link>
        </div>
      </section>
    );
  }

  return (
    <section className="register">
      <div className="register__main">
        <details className="register__faq">
          <summary>Before you start</summary>
          {REGISTER_FAQ.map((entry) => (
            <div key={entry.key} className="register__faq-entry">
              <h3>{entry.question}</h3>
              <p>{entry.answer}</p>
            </div>
          ))}
        </details>

        <div className="register__warning" role="note">
          <strong>Fraud warning</strong>
          <p>{REGISTER_COPY.fraudWarning}</p>
        </div>

        <RegisterSteps current={view.step} />

        <Panel controller={controller} />

        {view.error && (
          <p className="register__error" role="alert">
            {view.error}
          </p>
        )}

        <div className="register__actions">
          {view.stage !== "images" && (
            <button type="button" className="register__back" onClick={controller.back}>
              ← Back
            </button>
          )}
          {view.stage === "confirm" ? (
            <button
              type="button"
              className="register__next"
              disabled={view.busy}
              onClick={controller.submit}
            >
              ✦ Register this work
            </button>
          ) : (
            /* NO CONTINUE ON THE ARTIST QUESTION. OND-90: the step has exactly
               two answers, both of them in the panel, and a Continue beside
               them would be a third that walks past the question — which is
               the silent skip the ticket was filed about. Back is still there,
               so a misspelled name can be corrected rather than declined. */
            view.stage !== "artist-match" && (
              <button
                type="button"
                className="register__next"
                disabled={view.busy}
                onClick={controller.next}
              >
                Continue →
              </button>
            )
          )}
        </div>
      </div>
    </section>
  );
}

/** The one place a switch over stages lives. */
function Panel({ controller }: { controller: ReturnType<typeof useRegisterController> }) {
  const { view } = controller;
  if (view.kind !== "form") return null;

  switch (view.stage) {
    case "images":
      return (
        <UploadImagesPanel
          images={view.images}
          views={view.views}
          refusals={view.refusals}
          signed={view.values.signed}
          onFiles={controller.addImages}
          onRemove={controller.removeImage}
          onSetKind={controller.setImageKind}
          onToggleSigned={(signed) => controller.set("signed", signed)}
          onToggleView={controller.toggleView}
        />
      );
    case "details":
      return (
        <ArtworkDetailsForm
          values={view.values}
          targets={view.targets}
          artists={view.artists}
          routing={view.routing}
          onSet={controller.set}
          onSearch={controller.searchFor}
          onChooseArtist={controller.chooseArtist}
        />
      );
    // OND-90. "Did you mean one of these?"
    //
    // `artistMatch` is null on every other stage, and a null here would be a
    // list of candidates rendered over a field that has moved on — so the panel
    // is not rendered at all rather than rendered empty.
    case "artist-match":
      return view.artistMatch === null ? null : (
        <ArtistMatchPanel
          model={view.artistMatch}
          onChoose={controller.confirmArtist}
          onDecline={controller.declineArtists}
        />
      );
    case "provenance":
      return (
        <ProvenancePanel
          model={view.provenance}
          exhibitions={view.values.exhibitions}
          handlers={controller.provenance}
          spotlight={controller.spotlight}
          onCloseDocument={controller.closeDocument}
          onAdd={controller.addProvenanceEntry}
          onSetExhibitions={(value) => controller.set("exhibitions", value)}
        />
      );
    case "confirm":
      return (
        <ConfirmPanel
          rows={view.rows}
          declarations={view.declarations}
          onToggle={controller.toggleDeclaration}
        />
      );
    default:
      return assertNever(view.stage);
  }
}
